'use client';

/**
 * Expandable Text Component
 *
 * A client component that truncates long text and provides "See more" / "See less" toggle.
 * Used in PLPHero for long subtitles/descriptions.
 */

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import styles from './PlpHero.module.scss';

interface ExpandableTextProps {
  /** The full text content */
  text: string;
  /** Maximum characters to show when collapsed (default: 200) */
  maxLength?: number;
  /** CSS class for the text */
  className?: string;
  /** Inline styles for the text */
  style?: React.CSSProperties;
}

/**
 * Truncates text and adds "See more" / "See less" toggle
 */
export function ExpandableText({ text, maxLength = 200, className, style }: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations('common');

  // Only show toggle if text exceeds max length
  const needsTruncation = text.length > maxLength;

  // Truncate at word boundary
  const truncatedText =
    needsTruncation && !isExpanded ? text.slice(0, maxLength).replace(/\s+\S*$/, '') + '...' : text;

  return (
    <p className={className} style={style}>
      {truncatedText}
      {needsTruncation && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.expandButton}
            aria-expanded={isExpanded}
          >
            {isExpanded ? t('seeLess') : t('seeMore')}
          </button>
        </>
      )}
    </p>
  );
}

ExpandableText.displayName = 'ExpandableText';
